import { useEffect, useState } from 'react'

export interface Espacio {
  /** Ancho del elemento. */
  ancho: number
  /** Alto que le queda desde su borde de arriba hasta el pie de la ventana. */
  alto: number
}

/** Desde este ancho la vista se arma según la pantalla; más angosto, el diseño de siempre. */
export const ANCHO_ESCRITORIO = 1024

/**
 * Cuánto lugar le queda a un elemento en la pantalla, para que la grilla, el
 * calendario y el panel se armen según el monitor y no al revés (pedido de
 * Claudio del 23/09/2026: en un monitor de 1360×768 tiene que entrar todo sin
 * desplazarse).
 *
 * Sólo en escritorio: por debajo de 1024 px devuelve `null` y cada vista usa
 * su diseño de celular o tableta. Se vuelve a medir cuando cambia la ventana
 * y cuando algo de arriba corre al elemento (un aviso que aparece, un filtro).
 *
 * Se usa como `ref` callback: `const [ref, espacio] = useEspacioDisponible()`.
 */
export function useEspacioDisponible<T extends HTMLElement>(margenInferior = 16) {
  const [elemento, setElemento] = useState<T | null>(null)
  const [espacio, setEspacio] = useState<Espacio | null>(null)

  useEffect(() => {
    if (!elemento) return
    const medir = () => {
      if (window.innerWidth < ANCHO_ESCRITORIO) {
        setEspacio(null)
        return
      }
      const arriba = elemento.getBoundingClientRect().top + window.scrollY
      const siguiente = {
        ancho: elemento.clientWidth,
        alto: Math.max(0, Math.floor(window.innerHeight - arriba - margenInferior)),
      }
      setEspacio((previo) =>
        previo?.ancho === siguiente.ancho && previo.alto === siguiente.alto
          ? previo
          : siguiente,
      )
    }
    medir()
    const observador = new ResizeObserver(medir)
    observador.observe(elemento)
    // Lo de arriba puede crecer (el aviso de "N para revisar") y correrlo.
    observador.observe(document.body)
    window.addEventListener('resize', medir)
    return () => {
      observador.disconnect()
      window.removeEventListener('resize', medir)
    }
  }, [elemento, margenInferior])

  return [setElemento, espacio] as const
}
